<template>
  <tr v-if="!item.deleted">
    <td>
      <service-selector
        :default-service="item.service"
        :disabled="isElementDisabled('product')"
        @change="onServiceChange"
      />
    </td>
    <td>
      <service-pack-type-selector
        :default-pack-type="item.service.quantity_unit"
        :disabled="!item.service || !item.service.name || isElementDisabled('pack')"
        @change="onPackTypeChange"
      />
    </td>
    <td></td>
    <td
      v-if="isColumnDisplayed('count')"
    >
      <count
        :count="item.count"
        :item="item"
        :min-count="minCount"
        :disabled="!item.service || !item.service.name || isElementDisabled('count')"
        :compare-items="compareItems"
        :compare-type="compareType"
        @change="onCountChange"
      />
    </td>
    <td
      v-if="isColumnDisplayed('comment')"
    >
      <comment
        :item-comment="item.comment"
        :disabled="!item.service || !item.service.name || isElementDisabled('comment')"
        @change="onCommentChange"
      />
    </td>
    <td
      v-if="isColumnDisplayed('invoiceItemComment')"
    >
      <comment
        :item-comment="item.comment"
        :disabled="!item.service || !item.service.name || isElementDisabled('invoiceItemComment')"
        @change="onCommentChange"
      />
    </td>
    <td
      v-if="isColumnDisplayed('unitPrice')"
    >
      <unit-price
        :unit-price="item.unit_price"
        :currency="currency"
        :disabled="!item.service || !item.service.name || isElementDisabled('unitPrice')"
        @change="onUnitPriceChange"
      />
    </td>
    <td
      v-if="isColumnDisplayed('netPrice')"
    >
      <net-price
        :net-price="item.net_price"
        :currency="currency"
        :disabled="!item.service || !item.service.name || isElementDisabled('netPrice')"
        @change="onNetPriceChange"
      />
    </td>
    <td
      v-if="isColumnDisplayed('vat')"
    >
      <vat-selector
        :vat="item.vat"
        :disabled="!item.service || !item.service.name || isElementDisabled('vat')"
        @change="onVatChange"
      />
    </td>
    <td
      v-if="isColumnDisplayed('actions')"
      style="text-align: right;"
    >
      <actions
        :disabled="isElementDisabled('deleteButton')"
        @delete="onRowDelete"
      />
    </td>
  </tr>
</template>
<script>
import ServiceSelector from './columns/ServiceSelector';
import ServicePackTypeSelector from './columns/ServicePackTypeSelector';
import Count from './columns/Count';
import UnitPrice from './columns/UnitPrice';
import NetPrice from './columns/NetPrice';
import VatSelector from './columns/VatSelector';
import Actions from './columns/Actions';
import Comment from './columns/Comment';

export default {
  components: {
    ServiceSelector,
    ServicePackTypeSelector,
    Count,
    UnitPrice,
    NetPrice,
    VatSelector,
    Actions,
    Comment,
  },
  props: {
    defaultItem: {
      type: Object,
      default() {
        return {};
      },
      required: true,
    },
    minCount: {
      type: Number,
      default() {
        return 1;
      },
      required: false,
    },
    roundNet: {
      type: Boolean,
      default() {
        return false;
      },
      required: false,
    },
    currency: {
      type: String,
      default() {
        return 'HUF';
      },
      required: false,
    },
    defaultSupplier: {
      type: Object,
      default() {
        return null;
      },
      required: false,
    },
    enabledElements: {
      type: Array,
      default() {
        return ['addButton', 'deleteButton', 'product', 'pack', 'count', 'netPrice', 'vat', 'unitPrice', 'expirationDate', 'expirationDateSelector', 'supplierSelector'];
      },
      required: false,
    },
    enabledColumns: {
      type: Array,
      default() {
        return ['product', 'productPackType', 'expirationDate', 'supplierSelector', 'count', 'unitPrice', 'netPrice', 'vat', 'actions'];
      },
      required: false,
    },
    compareItems: {
      type: Array,
      default() {
        return null;
      },
      required: false,
    },
    compareType: {
      type: String,
      default() {
        return 'diff';
      },
      required: false,
    },
    isVatZero: {
      type: Boolean,
      default() {
        return false;
      },
      required: false,
    },
    isProductSelectFromStock: {
      type: Boolean,
      default() {
        return true;
      },
      required: false,
    },
    availableProducts: {
      validator: prop => typeof prop === 'object' || prop === null,
      default() {
        return null;
      },
      required: false,
    },
    loadBaseCountFromStock: {
      type: Boolean,
      default() {
        return true;
      },
      required: false,
    },
    storageId: {
      type: Number,
      default() {
        return 9;
      }
    },
  },
  data() {
    return {
      baseCountLoading: false,
      availableSuppliers: null,
      availableExpirationDates: [],
      item: {
        product: {},
        count: 1,
        unit_price: 0,
        net_price: 0,
        expiration_date: '',
        supplier_id: null,
        supplier: {},
        base_count: 0,
        comment: '',
      },
      expirationDates: [],
      expirationLoading: false,
      expirationDatesFetchFailed: false,
      packTypes: null,
      stockLoading: false,
      stockFetchFailed: false,
      fieldsValidation: {
        supplier: true,
        expirationDate: true,
      },
    };
  },
  beforeMount() {
    this.item = JSON.parse(JSON.stringify(this.defaultItem));
  },
  async mounted() {
    if (this.item.product && this.item.product.name && this.isProductSelectFromStock) {
      await this.fetchStock();
    }
  },
  methods: {
    async fetchStock() {
      this.stockLoading = true;
      try {
        this.packTypes = (await axios.get(`api/products/${this.item.product.id}/storage/${this.storageId}`)).packs;
        this.stockFetchFailed = false;
      } catch (e) {
        this.stockFetchFailed = true;
      }
      this.stockLoading = false;
    },
    async onPackTypeChange(packType) {
      this.$set(this.item, 'unit_of_measure', packType);
    },
    async onServiceChange(service) {
      this.item.service = service;
      if (!this.item.unit_price) {
        this.item.unit_price = parseFloat(this.item.service.price || 0);
      }
      if (this.roundNet) {
        this.item.net_price = Math.round(this.item.unit_price * (this.item.count || 1));
      } else {
        this.item.net_price = this.item.unit_price * (this.item.count || 1);
      }
      this.priceChange();
    },
    onCountChange(count) {
      this.item.count = count;
      this.setNetPrice();
    },
    onUnitPriceChange(unitPrice) {
      this.item.unit_price = unitPrice;
      this.setNetPrice();
    },
    onNetPriceChange(price) {
      if (this.roundNet) {
        this.item.net_price = Math.round(price);
      } else {
        this.item.net_price = price;
      }
      this.item.unit_price = parseFloat(this.item.net_price) / parseInt(this.item.count);
      this.priceChange();
    },
    onVatChange(vat) {
      this.item.vat = vat;
      this.priceChange();
    },
    onCommentChange(comment) {
      this.$set(this.item, 'comment', comment);
    },
    onRowDelete() {
      this.$set(this.item, 'deleted', true);
      this.priceChange();
    },
    setNetPrice() {
      if (this.roundNet) {
        this.item.net_price = Math.round(this.item.unit_price * this.item.count);
      } else {
        this.item.net_price = this.item.unit_price * this.item.count;
      }
      this.priceChange();
    },
    priceChange() {
      this.$emit('priceChange');
    },
    isElementDisabled(elementName) {
      return !this.enabledElements.includes(elementName);
    },
    isColumnDisplayed(column) {
      return this.enabledColumns.includes(column);
    },
    validateSupplier() {
      if (!this.item.supplier && !this.item.deleted) {
        this.fieldsValidation.supplier = false;
      }
      return this.fieldsValidation.supplier;
    },
    validateExpirationDate() {
      if (!this.item.expiration_date && !this.item.deleted) {
        this.fieldsValidation.expirationDate = false;
      }
      return this.fieldsValidation.expirationDate;
    },
    async loadBaseCount() {
      if (this.item.expiration_date && this.item.supplier.id && this.item.product_pack_type && this.storageId && this.loadBaseCountFromStock) {
        this.baseCountLoading = true;
        this.item.base_count = await axios.get(`api/storages/${this.storageId}/pack-type/${this.item.product_pack_type.id}/supplier/${this.item.supplier.id}/expiration/${this.item.expiration_date}`);
        this.baseCountLoading = false;
      }
    },
  },
};
</script>
